import {listedDiscountMeta,normalizeListedOffer} from '@/lib/badges/price-evidence'
/**
 * Centralized Badge Configuration
 * Single source of truth for ALL badge styling across the site
 * Colors match collection block designs for brand consistency
 */

export type BadgeType =
  | 'price_drop'      // 🔥 Priority #1
  | 'deal'            // 🎁 Value Pick
  | 'top_rated'       // ⭐ Top Rated
  | 'best_seller'     // Popular Pick (no emoji)
  | 'premium'         // 💎 Featured
  | 'trending'        // 🚀 Trending
  | 'energy_star'     // ⚡ Energy Star
  | 'new'             // ✨ New Product
  | 'low_stock'       // ⏳ Low Stock
  | 'seen_before'     // 🔍 Seen Before
  | null

export interface BadgeConfig {
  emoji: string
  label: string
  bgColor: string      // Tailwind class
  textColor: string    // Tailwind class
  borderColor?: string // Optional border
  priority: number     // Lower = higher priority
}

/**
 * Canonical Badge object — the single rendering contract.
 *
 * This is the shape produced server-side (see docs/Page-Types-And-Templates.md §3.1,
 * resolver task) and consumed by the shared <Badge /> / <BadgeList /> components.
 * Surfaces that only need to render read `type`, `label`, `emoji`, and the color
 * tokens. `meta` is additive and may be ignored.
 */
export interface Badge {
  type: Exclude<BadgeType, null> // stable machine key (NEVER localized)
  label: string                  // display text, e.g. "Price Drop"
  emoji: string                  // "" when none
  priority: number               // 1 = highest; render/sort ascending
  // presentation tokens (Tailwind classes; server payload stays string-only)
  bgColor: string
  textColor: string
  borderColor?: string
  // optional context payload for badges that need data (e.g. savings)
  meta?: {
    savingsPercent?: number    // price_drop / deal
    fromPriceCents?: number    // the "was" price used to compute the drop
    dealEndsAt?: string | null // deal
    stockQty?: number          // low_stock
  }
}

/**
 * Build a canonical Badge object from a BadgeType using BADGE_CONFIGS as the
 * source of presentation tokens. Optionally attach a `meta` payload.
 *
 * This lets any surface (or test) turn a bare type into a fully-formed Badge
 * that renders identically to a server-computed one.
 */
export function makeBadge(
  type: Exclude<BadgeType, null>,
  meta?: Badge['meta']
): Badge {
  const config = BADGE_CONFIGS[type]
  return {
    type,
    label: config.label,
    emoji: config.emoji,
    priority: config.priority,
    bgColor: config.bgColor,
    textColor: config.textColor,
    borderColor: config.borderColor,
    ...(meta ? { meta } : {}),
  }
}

// STANDARDIZED COLORS - Match collection blocks exactly
export const BADGE_CONFIGS: Record<Exclude<BadgeType, null>, BadgeConfig> = {
  // Priority 1: Price Drop (RED - outline style)
  price_drop: {
    emoji: '🔥',
    label: 'Listed discount',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    borderColor: 'border-red-200',
    priority: 1
  },
  
  // Priority 2: Value Pick (TEAL - solid)
  deal: {
    emoji: '🎁',
    label: 'Value Pick',
    bgColor: 'bg-teal-500',
    textColor: 'text-white',
    priority: 2
  },
  
  // Priority 3: Top Rated (PURPLE - solid)
  top_rated: {
    emoji: '⭐',
    label: 'Top Rated',
    bgColor: 'bg-purple-600',
    textColor: 'text-white',
    priority: 3
  },
  
  // Priority 4: Popular Pick (ORANGE - solid, no emoji)
  best_seller: {
    emoji: '',
    label: 'Popular Pick',
    bgColor: 'bg-orange-500',
    textColor: 'text-white',
    priority: 4
  },
  
  // Priority 5: Featured (PURPLE - solid)
  premium: {
    emoji: '💎',
    label: 'Featured',
    bgColor: 'bg-purple-600',
    textColor: 'text-white',
    priority: 5
  },
  
  // Priority 6: Trending (GREEN - solid). FRIDGE-141: green-700 — white text
  // on green-600 measured 3.22:1; on green-700 it clears 4.5:1.
  trending: {
    emoji: '🚀',
    label: 'Trending',
    bgColor: 'bg-green-700',
    textColor: 'text-white',
    priority: 6
  },
  
  // Priority 7: Energy Star (GREEN - solid). FRIDGE-141: green-700, see trending.
  energy_star: {
    emoji: '⚡',
    label: 'Energy Star',
    bgColor: 'bg-green-700',
    textColor: 'text-white',
    priority: 7
  },
  
  // Priority 8: New Product (BLUE - solid)
  new: {
    emoji: '✨',
    label: 'New Product',
    bgColor: 'bg-blue-600',
    textColor: 'text-white',
    priority: 8
  },
  
  // Priority 9: Low Stock (YELLOW - solid)
  low_stock: {
    emoji: '⏳',
    label: 'Low Stock',
    bgColor: 'bg-yellow-400',
    textColor: 'text-black',
    priority: 9
  },
  
  // Priority 10: Seen Before (GREEN - solid). FRIDGE-141: green-700, see trending.
  seen_before: {
    emoji: '🔍',
    label: 'Seen Before',
    bgColor: 'bg-green-700',
    textColor: 'text-white',
    priority: 10
  }
}

/**
 * Determine which badges to show for a product
 * Uses REAL Supabase data and PostHog analytics
 */
export function determineBadges(product: {
  // From Supabase products table
  lowest_price_cents?: number
  average_price_30d_cents?: number
  lowest_price_ever_cents?: number
  created_at?: string
  specs_json?: {
    rating?: number
    reviews?: number
    energy_star?: boolean
  }
  
  // From retailer_offers table
  availabilityMaxQty?: number
  dealBadge?: string
  
  // From PostHog (if available)
  clickCount?: number
  viewCount?: number
  
  // From offers array (product page specific)
  offers?: Array<{
    price?: number
    originalPrice?: number
    priceCents?: number
    originalPriceCents?: number
    price_cents?: number
    original_price_cents?: number
    priceStale?: boolean
    isPriceUnknown?: boolean
    inStock?: boolean
    status?: string
  }>
}): BadgeType[] {
  const badges: BadgeType[] = []
  
  // The stable price_drop key now describes only an explicit listed discount.
  // Bare average/lowest-ever columns cannot establish historical provenance.
  const firstOffer=product.offers?.[0]
  if(listedDiscountMeta(normalizeListedOffer(firstOffer),product.lowest_price_cents))badges.push('price_drop')

  // 2. Deal Badge (if retailer has special deal)
  if (product.dealBadge) {
    badges.push('deal')
  }
  
  // 3. Top Rated (rating >= 4.5 AND reviews > 100)
  if (product.specs_json?.rating && product.specs_json.rating >= 4.5) {
    if (product.specs_json?.reviews && product.specs_json.reviews > 100) {
      badges.push('top_rated')
    }
  }
  
  // 4. Best Seller (from PostHog click data)
  if (product.clickCount && product.clickCount > 100) {
    badges.push('best_seller')
  }
  
  // 5. Premium (price > $2000 AND rating >= 4.5)
  if (product.lowest_price_cents && product.lowest_price_cents > 200000) {
    if (product.specs_json?.rating && product.specs_json.rating >= 4.5) {
      badges.push('premium')
    }
  }
  
  // 6. Energy Star
  if (product.specs_json?.energy_star) {
    badges.push('energy_star')
  }
  
  // 7. New (created within last 30 days)
  if (product.created_at) {
    const daysSinceCreated = (Date.now() - new Date(product.created_at).getTime()) / (1000 * 60 * 60 * 24)
    if (daysSinceCreated <= 30) {
      badges.push('new')
    }
  }
  
  // 8. Low Stock (if quantity < 5)
  if (product.availabilityMaxQty && product.availabilityMaxQty < 5 && product.availabilityMaxQty > 0) {
    badges.push('low_stock')
  }
  
  // Sort by priority and return
  return badges.sort((a, b) => BADGE_CONFIGS[a].priority - BADGE_CONFIGS[b].priority)
}

/**
 * Get badge config for a specific type
 */
export function getBadgeConfig(badgeType: BadgeType): BadgeConfig | null {
  if (!badgeType) return null
  return BADGE_CONFIGS[badgeType]
}

